import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import NativeChoiceAnswers from './NativeChoiceAnswers';
import ProblemsProgress from './MathRodsProgress';
import { connect, useDispatch } from 'react-redux';
import ActivityButtons from './MathRodsButtons';
import { showStats, showFeedback, closeFeedback } from 'actions/gameActions';
import MathRodsFeedback from './MathRodsFeedback';
import { publishMathRodsStats } from './stats-bridge.mjs';
import useGetStringAudio from 'utils/hooks/useGetStringAudio';
import { s3bucketPublicURL } from 'config';
import * as buttons from 'consts/buttons';
import { mountMathRods } from './workspace.mjs';
import { playableContent } from './host-contract.mjs';
import { readMathRodsDocument } from './choice-translation.mjs';
import { createActivity, validateSegment } from './model.mjs';
import './MathRods.scss';
import { resolveNativeFeedback } from './native-feedback.mjs';
import { createNativeAudio } from './native-audio';
import { useMathRodsWriting } from './MathRodsWriting';
import writingActivityIcon from './assets/writing-activity.png';
import {fitMathRodsFrame} from './viewport-geometry.mjs';
import {createFrameScheduler} from './frame-scheduler.mjs';
import useActivityPageViewport from './useActivityPageViewport';
import useMathRodsTranslate from './useMathRodsTranslate';

// The host owns persistence/localization and renders its existing native editors.
export const MathRodsWorkspace = ({
  data, author = false, onChange, audioBase = '', identity = '', embedded = false,
  externalControls = false, onComplete, onStateChange, onReady, onFeedback, onHelp,
  renderQuestionEditor, renderSegmentAudio, externalSegmentList = false, activeSegmentId, translate,
}) => {
  const [choiceDisplay, setChoiceDisplay] = useState(null);
  const [progressDisplay, setProgressDisplay] = useState(null);
  const renderProgress = useCallback((container, props) => {
    setProgressDisplay({ container, props });
    return { destroy: () => setProgressDisplay(null) };
  }, []);
  const renderChoiceAnswers = useCallback((container, props) => {
    setChoiceDisplay({container, props});
    return {destroy: () => setChoiceDisplay(null)};
  }, []);
  const host = useRef(null);
  const instance = useRef(null);
  const handlers = useRef({});
  handlers.current = { onChange, onComplete, onStateChange, onReady, onFeedback,
    onHelp, renderQuestionEditor, renderSegmentAudio };
  useEffect(() => {
    const workspace = mountMathRods(host.current, {
      data: data?.problems?.length ? { ...data, schemaVersion: 1 } : createActivity(),
      author, audioBase, embedded, externalControls, externalSegmentList,
      createAudio: createNativeAudio, renderChoiceAnswers, renderProgress, translate,
      onChange: value => handlers.current.onChange?.(value),
      onComplete: value => handlers.current.onComplete?.(value),
      onStateChange: value => handlers.current.onStateChange?.(value),
      onFeedback: onFeedback ? value => handlers.current.onFeedback?.(value) : null,
      onHelp: onHelp ? () => handlers.current.onHelp?.() : null,
      renderQuestionEditor: renderQuestionEditor
        ? (...args) => handlers.current.renderQuestionEditor?.(...args) : null,
      renderSegmentAudio: renderSegmentAudio
        ? (...args) => handlers.current.renderSegmentAudio?.(...args) : null,
    });
    instance.current = workspace;
    if (externalSegmentList) workspace.updateAuthorData(data, activeSegmentId);
    handlers.current.onReady?.(workspace);
    return () => { handlers.current.onReady?.(null); workspace.destroy(); instance.current = null; };
    // Remount on FLC locale/translator changes as well as activity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, author, audioBase, embedded, externalControls, externalSegmentList]);
  // Catalog loading refreshes labels without resetting the learner's rods or ledger.
  useLayoutEffect(() => { instance.current?.setTranslate(translate); }, [translate]);
  useLayoutEffect(() => {
    if (externalSegmentList) instance.current?.updateAuthorData(data, activeSegmentId);
  }, [data, activeSegmentId, externalSegmentList]);
  return <><div className="math-rods-workspace-host" ref={host} />{choiceDisplay && createPortal(<NativeChoiceAnswers {...choiceDisplay.props} />, choiceDisplay.container)}{progressDisplay && createPortal(<ProblemsProgress {...progressDisplay.props} />, progressDisplay.container)}</>;
};

export function MathRods({
  activity, versionType = '', contentFolder = '', gameInitialized,
  translate,
}) {
  const dispatch = useDispatch();
  const [privateFeedback, setPrivateFeedback] = useState(null);
  const [runtimeState, setRuntimeState] = useState({});
  const controller = useRef(null);
  const stageRef = useRef(null);
  const fitRef = useRef(null);
  const pageRef = useRef(null);
  const mobileLayout = useActivityPageViewport(pageRef);
  const content = readMathRodsDocument(playableContent(activity?.data, versionType, validateSegment));
  // Reserve the native button bar, then scale the whole lesson consistently.
  // A smaller viewport never hides the lesson or introduces an inner scroller.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    const root = stage?.parentElement;
    if (!root) return;
    let bar;
    let viewportKey;
    let reservedPromptHeight = 0;
    const frames = createFrameScheduler(measure);
    const schedule = frames.schedule;
    const observer = new ResizeObserver(schedule);
    function measure() {
        const nextBar = root.querySelector(':scope > [class*="outerClass"]');
        if (nextBar !== bar) {
          if (bar) observer.unobserve(bar);
          bar = nextBar;
          if (bar) observer.observe(bar);
        }
        if (!bar || stage.hidden) return;
        const rootRect = root.getBoundingClientRect();
        const barRect = bar.getBoundingClientRect();
        const margin = mobileLayout ? 6 : rootRect.width <= 1100 || rootRect.height <= 740 ? 16 : 50;
        stage.style.inset = mobileLayout
          ? `calc(var(--flc-mobile-safe-top) + 6px) calc(var(--flc-mobile-safe-right) + 6px) ${rootRect.bottom - barRect.top + margin}px calc(var(--flc-mobile-safe-left) + 6px)`
          : `${margin}px ${margin}px ${rootRect.bottom - barRect.top + margin}px`;
        const bounds = stage.getBoundingClientRect();
        const nextViewportKey = `${rootRect.width}:${rootRect.height}`;
        if (nextViewportKey !== viewportKey) {
          viewportKey = nextViewportKey;
          reservedPromptHeight = 0;
        }
        // Scaling changes the logical text width. Keep the largest measured
        // height until the actual viewport changes, avoiding a wrap/scale loop
        // where a three-line prompt repeatedly becomes four lines and back.
        reservedPromptHeight = Math.max(reservedPromptHeight, Number(root.querySelector('.mr')?.dataset.promptMeasuredHeight) || 128);
        // Production mobile sizes the board to the available screen. Keep the
        // authored scene, readable prompt and shared controls at independent
        // sizes instead of shrinking the entire desktop UI on a phone.
        const fit = mobileLayout ? {width:bounds.width,height:bounds.height,scale:1}
          : fitMathRodsFrame({width: bounds.width, height: bounds.height, promptHeight: reservedPromptHeight});
        Object.assign(fitRef.current.style, {width: `${fit.width}px`, height: `${fit.height}px`, position: 'absolute', left: `${Math.max(0,(bounds.width-fit.width*fit.scale)/2)}px`, transform: `scale(${fit.scale})`});
        fitRef.current.dataset.frameScale = String(fit.scale);
    }
    observer.observe(root);
    const children = new MutationObserver(schedule);
    children.observe(root, {childList: true, subtree: true});
    root.addEventListener('math-rods-prompt-resize', schedule);
    schedule();
    return () => { frames.dispose(); observer.disconnect(); children.disconnect(); root.removeEventListener('math-rods-prompt-resize', schedule); };
  }, [mobileLayout]);
  const hasPlayableSegments = content.problems.length > 0;
  const runtimeData = {
    ...content,
    language: (versionType ? versionType.slice(versionType.indexOf('_') + 1) : '')
      || content.language || activity?.activity?.locale || 'en',
    options: { ...content.options, ...activity?.data?.options },
  };
  const catalogTranslate = useMathRodsTranslate(runtimeData.language, translate);
  const writing = useMathRodsWriting({activity, language: runtimeData.language, translate});
  useEffect(() => { controller.current?.setSuspended(writing.active || writing.busy); }, [writing.active, writing.busy]);
  const getStringAudio = useGetStringAudio(runtimeData.language);
  const nativeFeedback = feedback => setPrivateFeedback(resolveNativeFeedback(feedback, {
    translate, getStringAudio, language: runtimeData.language, currentActivity: activity, s3bucketPublicURL,
  }));
  const publishNativeStats = results => publishMathRodsStats(results, {
    dispatch, showFeedback, closeFeedback, showStats,
  });
  const invoke = method => () => controller.current?.[method]();
  return (
    <div className="math-rods-native-play" ref={pageRef}>
      <div className="math-rods-native-stage" ref={stageRef} hidden={writing.active}>
        <div className="math-rods-play-fit" ref={fitRef}>
        {gameInitialized && hasPlayableSegments && (
          <MathRodsWorkspace
            data={runtimeData}
            translate={catalogTranslate}
            identity={`${activity?.activity?._id || activity?._id || ''}:${versionType}`}
            audioBase={contentFolder}
            embedded externalControls
            onReady={workspace => { controller.current = workspace; }}
            onStateChange={setRuntimeState}
            onComplete={publishNativeStats}
            onFeedback={nativeFeedback}
          />
        )}
        {gameInitialized && !hasPlayableSegments && (
          <p className="math-rods-empty-play" role="status">
            {translate(0, 'There are no completed segments in this language yet.')}
          </p>
        )}

        </div>
      </div>
      {privateFeedback && <MathRodsFeedback feedback={privateFeedback} versionType={versionType}
        contentFolder={contentFolder || activity?.activity?.contentFolder || ''}
        language={runtimeData.language} translate={translate}
        onClose={() => { const current = privateFeedback; setPrivateFeedback(null); current.onClose?.(); }} />}
      {writing.error && !writing.active && <p role="alert" className="math-rods-writing-error">{writing.error}</p>}
      {writing.screen}
      {!writing.active && <ActivityButtons disabledExcept={writing.busy ? [] : runtimeState.mediaPlaying ? [
        ...(runtimeState.demoPlaying ? [buttons.STOP] : []),
        ...(runtimeState.audioPlaying && !runtimeState.hasDemo ? [buttons.SPEAK] : []),
      ] : undefined} buttons={[
        { type: buttons.GO_BACK, onClick: runtimeState.canCompleteOnExit ? invoke('completeOnExit') : undefined },
        { type: 'MATH_RODS_WRITING', globalButtonAlias: buttons.EBOOK_BUTTON, position: 2,
          iconUrl: writingActivityIcon,
          titleName: translate(0, 'Writing Activity'), titleStringNumber: -1,
          onClick: () => writing.open(controller.current?.getWritingSnapshot()),
          dontShow: !gameInitialized || !hasPlayableSegments || !runtimeState.learnerObjectCount || runtimeState.completed,
          disabled: runtimeState.locked || writing.busy },
        { type: runtimeState.demoPlaying ? buttons.STOP : buttons.GO,
          onClick: invoke('toggleDemo'),
          dontShow: !gameInitialized || !hasPlayableSegments || (!runtimeState.hasDemo || runtimeState.completed),
          disabled: runtimeState.locked && !runtimeState.demoPlaying },
        { type: buttons.CORRECT_ANSWER, onClick: invoke('revealAnswer'),
          dontShow: !gameInitialized || !hasPlayableSegments || !runtimeState.canReveal },
        { type: buttons.CHECK_ANSWER, onClick: invoke('checkRods'),
          dontShow: !gameInitialized || !hasPlayableSegments || !runtimeState.canCheck },
        { type: buttons.SPEAK, onClick: invoke('playAudio'),
          dontShow: !gameInitialized || !hasPlayableSegments || (!runtimeState.hasAudio || runtimeState.hasDemo || runtimeState.completed),
          disabled: runtimeState.locked && !runtimeState.audioPlaying },
        buttons.HELP,
      ]} />}
    </div>
  );
}

export default connect(({ game }) => ({ gameInitialized: game.gameInitialized }))(MathRods);
